import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useRouter } from '@tanstack/react-router';
import { ApiError } from '@shared/api';

/** Stable identities and exact SKU evidence are kept separate. */
export interface StockSourceVariant {
  variantId: string;
  shopifySku: string;
  inventoryItemId: string | null;
  identityStatus: 'valid' | 'missing' | 'ambiguous' | 'untracked';
}

export interface StockSourceProduct {
  productId: string;
  sourceSku: string;
  label: string;
  available: boolean;
}

export interface StockSourceRule {
  variantId: string;
  sourceProductId: string;
  kind: 'override' | 'shared';
  sharedGroupId: string | null;
  observedShopifySku?: string;
  observedSourceSku?: string;
}

/** Exact catalog evidence submitted with each stable-ID relationship. */
export interface StockSourceSaveRule extends StockSourceRule {
  observedShopifySku: string;
  observedSourceSku: string;
}

export interface StockSourceCandidate {
  variant: StockSourceVariant;
  kind: 'override' | 'shared';
  sharedGroupId: string | null;
  suggestedProductId: string | null;
  suggestedSourceSku: string;
}

export interface StockSourcesSnapshot {
  scope: {
    tenantId: string;
    environment: string;
    storeDomain: string;
  };
  revision: number;
  /** Strong, opaque server validator to pass unchanged as If-Match. */
  etag: string;
  updatedAt: string | null;
  updatedBy: string | null;
  canSave: boolean;
  variants: readonly StockSourceVariant[];
  products: readonly StockSourceProduct[];
  savedRules: readonly StockSourceRule[];
  candidates: readonly StockSourceCandidate[];
  validation?: { valid: boolean; issues: readonly { ruleIndex: number | null; code: string }[] };
}

export interface StockSourcesPort {
  load(): Promise<StockSourcesSnapshot>;
  save(input: {
    ifMatch: string;
    rules: readonly StockSourceSaveRule[];
  }): Promise<StockSourcesSnapshot>;
}

/** A rejected revision must be handled by the caller, never retried automatically. */
export class StockSourcesConflictError extends Error {
  constructor() {
    super('The saved mapping changed. Review the latest revision before saving again.');
    this.name = 'StockSourcesConflictError';
  }
}

const PAGE_SIZE = 10;
type StockSourcesErrorKey =
  | 'shopifyManagement.stockSources.accessDenied'
  | 'shopifyManagement.stockSources.unavailable'
  | 'shopifyManagement.stockSources.scopeChanged'
  | 'shopifyManagement.stockSources.conflict'
  | 'shopifyManagement.stockSources.refreshError'
  | 'shopifyManagement.stockSources.saveError';

function rulesEqual(left: readonly StockSourceRule[], right: readonly StockSourceRule[]): boolean {
  const normalize = (rules: readonly StockSourceRule[]) =>
    rules
      .map(({ variantId, sourceProductId, kind, sharedGroupId }) => ({
        variantId,
        sourceProductId,
        kind,
        sharedGroupId,
      }))
      .sort((a, b) => a.variantId.localeCompare(b.variantId));
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}

function sameRelationship(left: StockSourceRule, right: StockSourceRule): boolean {
  return (
    left.variantId === right.variantId &&
    left.sourceProductId === right.sourceProductId &&
    left.kind === right.kind &&
    left.sharedGroupId === right.sharedGroupId
  );
}

function scopeKey(snapshot: StockSourcesSnapshot): string {
  return Object.values(snapshot.scope).join('\u0000');
}

function isStrongEtag(etag: string): boolean {
  return etag.startsWith('"') && etag.endsWith('"') && etag.length > 2 && !etag.startsWith('W/');
}

function NavigationGuard({ dirty, message }: { dirty: boolean; message: string }) {
  useBlocker({ shouldBlockFn: () => dirty && !window.confirm(message), enableBeforeUnload: false });
  return null;
}

/** Standalone local-configuration editor. It has no Shopify write capability or API binding. */
export function StockSourcesEditor({
  port,
  onDirtyChange,
}: {
  port: StockSourcesPort;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const hasRouter = Boolean(useRouter({ warn: false }));
  const [snapshot, setSnapshot] = useState<StockSourcesSnapshot | null>(null);
  const [draft, setDraft] = useState<readonly StockSourceRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<StockSourcesErrorKey | null>(null);
  const [conflict, setConflict] = useState(false);
  const [scopeChanged, setScopeChanged] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const dirty = snapshot !== null && !rulesEqual(draft, snapshot.savedRules);

  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return;
    const protect = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', protect);
    return () => {
      window.removeEventListener('beforeunload', protect);
    };
  }, [dirty]);

  useEffect(() => {
    const request = { active: true };
    void (async () => {
      try {
        const result = await port.load();
        if (!request.active) return;
        setSnapshot(result);
        setDraft(result.savedRules);
        setError(null);
        setLoading(false);
      } catch (cause) {
        if (!request.active) return;
        setError(
          cause instanceof ApiError && [401, 403].includes(cause.status)
            ? 'shopifyManagement.stockSources.accessDenied'
            : 'shopifyManagement.stockSources.unavailable'
        );
        setLoading(false);
      }
    })();
    return () => {
      request.active = false;
    };
  }, [port]);

  const rows = useMemo(() => {
    if (!snapshot) return [];
    const candidateById = new Map(
      snapshot.candidates.map((candidate) => [candidate.variant.variantId, candidate])
    );
    const variantById = new Map(snapshot.variants.map((variant) => [variant.variantId, variant]));
    const ids = new Set([
      ...variantById.keys(),
      ...candidateById.keys(),
      ...snapshot.savedRules.map((rule) => rule.variantId),
    ]);
    const query = search.toLocaleLowerCase();
    return [...ids].flatMap((id) => {
      const saved = snapshot.savedRules.find((rule) => rule.variantId === id);
      const candidate = candidateById.get(id);
      const variant =
        variantById.get(id) ??
        candidate?.variant ??
        (saved
          ? {
              variantId: id,
              shopifySku: saved.observedShopifySku ?? '',
              inventoryItemId: null,
              identityStatus: 'missing' as const,
            }
          : null);
      if (!variant) return [];
      if (
        ![id, variant.shopifySku, saved?.sourceProductId, candidate?.suggestedSourceSku].some(
          (value) => value?.toLocaleLowerCase().includes(query)
        )
      )
        return [];
      return [{ variant, saved, candidate, selected: draft.find((rule) => rule.variantId === id) }];
    });
  }, [snapshot, draft, search]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visibleRows = rows.slice(
    (Math.min(page, totalPages) - 1) * PAGE_SIZE,
    Math.min(page, totalPages) * PAGE_SIZE
  );
  const productsById = new Map(
    snapshot?.products.map((product) => [product.productId, product]) ?? []
  );
  const sharedGroups = new Set(
    snapshot?.candidates
      .filter((candidate) => candidate.kind === 'shared')
      .map((candidate) => candidate.sharedGroupId)
      .filter((groupId): groupId is string => groupId !== null) ?? []
  );
  const invalid = draft.some((rule) => {
    const variant = snapshot?.variants.find((item) => item.variantId === rule.variantId);
    const product = productsById.get(rule.sourceProductId);
    return variant?.identityStatus !== 'valid' || !product?.available;
  });
  const missingObservation = draft.some((rule) => {
    const saved = snapshot?.savedRules.find((item) => sameRelationship(item, rule));
    return (
      saved !== undefined &&
      (saved.observedShopifySku === undefined || saved.observedSourceSku === undefined)
    );
  });
  const invalidRelationship =
    new Set(draft.map((rule) => rule.variantId)).size !== draft.length ||
    draft.some(
      (rule) =>
        (rule.kind === 'shared' && !rule.sharedGroupId) ||
        (rule.kind === 'override' && rule.sharedGroupId !== null)
    );
  const missingValidator = snapshot !== null && !isStrongEtag(snapshot.etag);
  const incompleteSharedGroup = [...sharedGroups].some((groupId) => {
    const members =
      snapshot?.candidates.filter(
        (candidate) =>
          candidate.sharedGroupId === groupId && candidate.variant.identityStatus === 'valid'
      ) ?? [];
    const selected = members.map((member) =>
      draft.find((rule) => rule.variantId === member.variant.variantId)
    );
    const selectedCount = selected.filter(Boolean).length;
    return (
      selectedCount > 0 &&
      (selectedCount !== members.length ||
        new Set(selected.map((rule) => rule?.sourceProductId)).size !== 1)
    );
  });

  async function refresh(discard = false) {
    setLoading(true);
    try {
      const result = await port.load();
      if (snapshot && scopeKey(snapshot) !== scopeKey(result) && dirty && !discard) {
        setScopeChanged(true);
        setError('shopifyManagement.stockSources.scopeChanged');
        return;
      }
      if (snapshot && result.revision !== snapshot.revision && dirty && !discard) {
        setSnapshot(result);
        setConflict(true);
        setError('shopifyManagement.stockSources.conflict');
        return;
      }
      if (discard || !dirty) setDraft(result.savedRules);
      setSnapshot(result);
      setConflict(false);
      setScopeChanged(false);
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof ApiError && [401, 403].includes(cause.status)
          ? 'shopifyManagement.stockSources.accessDenied'
          : 'shopifyManagement.stockSources.refreshError'
      );
    } finally {
      setLoading(false);
    }
  }

  function choose(variantId: string, sourceProductId: string) {
    if (!snapshot) return;
    const candidate = snapshot.candidates.find((item) => item.variant.variantId === variantId);
    const existing = draft.find((item) => item.variantId === variantId);
    const next = draft.filter((item) => item.variantId !== variantId);
    if (sourceProductId) {
      next.push({
        variantId,
        sourceProductId,
        kind: existing?.kind ?? candidate?.kind ?? 'override',
        sharedGroupId: existing?.sharedGroupId ?? candidate?.sharedGroupId ?? null,
      });
    }
    setDraft(next);
  }

  async function save() {
    if (
      !snapshot ||
      !dirty ||
      invalid ||
      missingObservation ||
      invalidRelationship ||
      missingValidator ||
      incompleteSharedGroup ||
      conflict ||
      scopeChanged ||
      !snapshot.canSave ||
      saving
    )
      return;
    setSaving(true);
    setError(null);
    try {
      const rules = draft.map((rule): StockSourceSaveRule => {
        const saved = snapshot.savedRules.find((item) => sameRelationship(item, rule));
        return {
          ...rule,
          observedShopifySku:
            saved?.observedShopifySku ??
            snapshot.variants.find((variant) => variant.variantId === rule.variantId)?.shopifySku ??
            '',
          observedSourceSku:
            saved?.observedSourceSku ?? productsById.get(rule.sourceProductId)?.sourceSku ?? '',
        };
      });
      const saved = await port.save({ ifMatch: snapshot.etag, rules });
      setSnapshot(saved);
      setDraft(saved.savedRules);
      setConflict(false);
    } catch (cause) {
      if (cause instanceof StockSourcesConflictError) {
        setConflict(true);
        setError('shopifyManagement.stockSources.conflict');
      } else if (cause instanceof ApiError && [401, 403].includes(cause.status)) {
        setSnapshot({ ...snapshot, canSave: false });
        setError('shopifyManagement.stockSources.accessDenied');
      } else {
        setError('shopifyManagement.stockSources.saveError');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-label={t('shopifyManagement.stockSources.tab')} className="space-y-4">
      {hasRouter && (
        <NavigationGuard dirty={dirty} message={t('shopifyManagement.stockSources.leaveConfirm')} />
      )}
      <p className="text-sm text-muted-foreground">
        {t('shopifyManagement.stockSources.savePolicy')}
      </p>
      {snapshot && (
        <p className="text-sm">
          {t('shopifyManagement.stockSources.scopeSummary', {
            store: snapshot.scope.storeDomain,
            environment: snapshot.scope.environment,
            revision: snapshot.revision,
          })}
          {snapshot.updatedAt &&
            ` · ${t('shopifyManagement.stockSources.savedAt', {
              date: new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(snapshot.updatedAt)),
              actor: snapshot.updatedBy ?? t('shopifyManagement.stockSources.unknownActor'),
            })}`}
        </p>
      )}
      {snapshot && (
        <p className="text-sm">
          {t('shopifyManagement.stockSources.reviewSummary', {
            shared: sharedGroups.size,
            overrides: snapshot.candidates.filter((item) => item.kind === 'override').length,
            saved: snapshot.savedRules.length,
          })}
        </p>
      )}
      {dirty && <p role="status">{t('shopifyManagement.stockSources.unsaved')}</p>}
      {error && (
        <p role="alert" className="text-destructive">
          {t(error)}
        </p>
      )}
      {!snapshot && loading && <p role="status">{t('shopifyManagement.stockSources.loading')}</p>}
      {!snapshot && !loading && (
        <button type="button" onClick={() => void refresh()}>
          {t('shopifyManagement.stockSources.retry')}
        </button>
      )}
      {snapshot && (
        <>
          <div className="flex flex-wrap gap-2">
            <label>
              {t('shopifyManagement.stockSources.search')}{' '}
              <input
                type="search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </label>
            <button type="button" disabled={loading || saving} onClick={() => void refresh()}>
              {t('shopifyManagement.stockSources.refresh')}
            </button>
            <button
              type="button"
              disabled={!dirty || saving || loading}
              onClick={() => void refresh(true)}
            >
              {t('shopifyManagement.stockSources.cancel')}
            </button>
            <button
              type="button"
              disabled={
                !dirty ||
                invalid ||
                missingObservation ||
                invalidRelationship ||
                missingValidator ||
                incompleteSharedGroup ||
                conflict ||
                scopeChanged ||
                saving ||
                loading ||
                !snapshot.canSave
              }
              onClick={() => void save()}
            >
              {saving
                ? t('shopifyManagement.stockSources.saving')
                : t('shopifyManagement.stockSources.save')}
            </button>
          </div>
          {!snapshot.canSave && <p role="status">{t('shopifyManagement.stockSources.readOnly')}</p>}
          {invalid && <p role="alert">{t('shopifyManagement.stockSources.invalidSource')}</p>}
          {missingObservation && (
            <p role="alert">{t('shopifyManagement.stockSources.missingObservation')}</p>
          )}
          {snapshot.validation?.valid === false && (
            <p role="alert">{t('shopifyManagement.stockSources.savedValidationIssues')}</p>
          )}
          {invalidRelationship && (
            <p role="alert">{t('shopifyManagement.stockSources.invalidRelationship')}</p>
          )}
          {missingValidator && (
            <p role="alert">{t('shopifyManagement.stockSources.missingValidator')}</p>
          )}
          {incompleteSharedGroup && (
            <p role="alert">{t('shopifyManagement.stockSources.incompleteSharedGroup')}</p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">
                {t('shopifyManagement.stockSources.tableCaption')}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('shopifyManagement.stockSources.variant')}</th>
                  <th scope="col">{t('shopifyManagement.stockSources.relationship')}</th>
                  <th scope="col">{t('shopifyManagement.stockSources.sourceProduct')}</th>
                  <th scope="col">{t('shopifyManagement.stockSources.validation')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map(({ variant, saved, candidate, selected }) => {
                  const source = saved && productsById.get(saved.sourceProductId);
                  const savedRuleIndex = saved ? snapshot.savedRules.indexOf(saved) : -1;
                  return (
                    <tr key={variant.variantId}>
                      <td>
                        {variant.shopifySku || t('shopifyManagement.stockSources.noSku')}
                        <br />
                        <code>{variant.variantId}</code>
                        {variant.inventoryItemId && (
                          <>
                            <br />
                            <small>
                              {t('shopifyManagement.stockSources.inventoryItem', {
                                id: variant.inventoryItemId,
                              })}
                            </small>
                          </>
                        )}
                      </td>
                      <td>
                        {saved
                          ? t('shopifyManagement.stockSources.savedRelationship', {
                              kind: saved.kind,
                              group: saved.sharedGroupId ? ` · ${saved.sharedGroupId}` : '',
                            })
                          : candidate
                            ? t('shopifyManagement.stockSources.candidateRelationship', {
                                kind: candidate.kind,
                                group: candidate.sharedGroupId
                                  ? ` · ${candidate.sharedGroupId}`
                                  : '',
                              })
                            : t('shopifyManagement.stockSources.exactOrUnmapped')}
                      </td>
                      <td>
                        {saved && (
                          <>
                            <small>
                              {t('shopifyManagement.stockSources.savedSource', {
                                sku:
                                  source?.sourceSku ??
                                  t('shopifyManagement.stockSources.missingSource'),
                                id: saved.sourceProductId,
                              })}
                            </small>
                            {saved.observedShopifySku !== undefined &&
                              saved.observedSourceSku !== undefined && (
                                <small className="block text-muted-foreground">
                                  {t('shopifyManagement.stockSources.observedSkus', {
                                    shopifySku: saved.observedShopifySku,
                                    sourceSku: saved.observedSourceSku,
                                  })}
                                </small>
                              )}
                          </>
                        )}
                        {candidate && !saved && (
                          <small>
                            {t('shopifyManagement.stockSources.suggestedSource', {
                              sku:
                                candidate.suggestedSourceSku ||
                                t('shopifyManagement.stockSources.noSku'),
                              id:
                                candidate.suggestedProductId ??
                                t('shopifyManagement.stockSources.unresolvedIdentity'),
                            })}
                          </small>
                        )}
                        <br />
                        <label>
                          {t('shopifyManagement.stockSources.selectedSource', {
                            id: variant.variantId,
                          })}{' '}
                          <select
                            value={selected?.sourceProductId ?? ''}
                            disabled={
                              !snapshot.canSave ||
                              loading ||
                              saving ||
                              (variant.identityStatus !== 'valid' && !selected)
                            }
                            onChange={(event) => {
                              choose(variant.variantId, event.target.value);
                            }}
                          >
                            <option value="">
                              {t('shopifyManagement.stockSources.noSavedRule')}
                            </option>
                            {snapshot.products
                              .filter(
                                (product) =>
                                  product.available ||
                                  product.productId === selected?.sourceProductId
                              )
                              .map((product) => (
                                <option
                                  key={product.productId}
                                  value={product.productId}
                                  disabled={variant.identityStatus !== 'valid'}
                                >
                                  {product.label} · {product.sourceSku} · {product.productId}
                                  {product.available
                                    ? ''
                                    : t('shopifyManagement.stockSources.unavailableSuffix')}
                                </option>
                              ))}
                          </select>
                        </label>
                      </td>
                      <td>
                        {saved &&
                        snapshot.validation?.issues.some(
                          (issue) => issue.ruleIndex === savedRuleIndex
                        )
                          ? t('shopifyManagement.stockSources.savedRuleNeedsReview')
                          : variant.identityStatus === 'valid'
                            ? selected && !productsById.get(selected.sourceProductId)?.available
                              ? t('shopifyManagement.stockSources.missingSourceProduct')
                              : t('shopifyManagement.stockSources.identityValid')
                            : t(
                                `shopifyManagement.stockSources.identity.${variant.identityStatus}`
                              )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && <p>{t('shopifyManagement.stockSources.empty')}</p>}
          </div>
          <nav
            aria-label={t('shopifyManagement.stockSources.pages')}
            className="flex items-center gap-2"
          >
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => {
                setPage(page - 1);
              }}
            >
              {t('shopifyManagement.stockSources.previous')}
            </button>
            <span>
              {t('shopifyManagement.stockSources.page', {
                page: Math.min(page, totalPages),
                totalPages,
              })}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => {
                setPage(page + 1);
              }}
            >
              {t('shopifyManagement.stockSources.next')}
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
